import { useEffect, useMemo, useState, useCallback } from 'react';
import { CONSTANTS } from '../data';
import { GameState } from '../systems/GameState';
import { AwarenessSystem } from '../systems/AwarenessSystem';
import { EffectsSystem } from '../systems/EffectsSystem';
import { CombinationEngine } from '../game/CombinationEngine';
import { ChainReactionSystem } from '../game/ChainReactionSystem';
import { AchievementSystem } from '../game/AchievementSystem';
import { MemoryFragmentSystem, type MemoryFragment } from '../systems/MemoryFragmentSystem';
import { sounds } from '../systems/AudioSystem';
import { getDiscoveryDialogue } from '../systems/DialogueSystem';
import { typeAIMessage } from '../utils/typeText';
import { EnvironmentalHorrorSystem } from '../systems/EnvironmentalHorrorSystem';
import { VirusSystem } from '../game/VirusSystem';

type GameStateKey = 'loading' | 'splash' | 'story' | 'menu' | 'playing' | 'instructions' | 'credits' | 'secret_ending' | 'game_over' | 'ending';

export function useGameController() {
  // Game state
  const [gameState, setGameState] = useState<GameStateKey>('loading');
  const [fusedElements, setFusedElements] = useState<string[]>([]);
  const [discoveredCombinations, setDiscoveredCombinations] = useState<Set<string>>(new Set());
  const [unlockedAchievements, setUnlockedAchievements] = useState<Set<string>>(new Set());
  const [achievementPopup, setAchievementPopup] = useState<{id: string, name: string, description: string} | null>(null);
  const [shownChapters, setShownChapters] = useState<Set<string>>(new Set());
  const [currentChapter, setCurrentChapter] = useState<{id: string, title: string, text: string} | null>(null);
  const [activeEnding, setActiveEnding] = useState<string | null>(null);

  // Moral choice state
  const [shownMoralEvents, setShownMoralEvents] = useState<Set<string>>(new Set());
  const [currentMoralEvent, setCurrentMoralEvent] = useState<any | null>(null);

  // Memory fragment state
  const [currentMemoryFragment, setCurrentMemoryFragment] = useState<MemoryFragment | null>(null);

  // Chat modal state
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{text: string, sender: 'user' | 'ai'}>>([]);
  const [chatInput, setChatInput] = useState('');
  const [isAiTyping, setIsAiTyping] = useState(false);

  // Environmental horror state
  const [environmentalMessage, setEnvironmentalMessage] = useState<any | null>(null);
  const [screenFlash, setScreenFlash] = useState<string | null>(null);

  // Virus state
  const [corruptedElements, setCorruptedElements] = useState<Set<string>>(new Set());
  const [showVirusPuzzle, setShowVirusPuzzle] = useState(false);
  const [virusPuzzleGrid, setVirusPuzzleGrid] = useState<number[][]>([]);
  const [solvedVirusPatterns, setSolvedVirusPatterns] = useState<Set<string>>(new Set());
  const [virusPuzzleTimer, setVirusPuzzleTimer] = useState(CONSTANTS.VIRUS_PUZZLE.timerDuration);
  const [virusConsequenceTimer, setVirusConsequenceTimer] = useState(CONSTANTS.VIRUS.CONSEQUENCE_TIMER_SECONDS);
  const [virusPuzzleHintCells, setVirusPuzzleHintCells] = useState<{row: number, col: number}[]>([]);
  const [virusInterferenceInterval, setVirusInterferenceInterval] = useState<NodeJS.Timeout | null>(null);

  // Reset confirmation state
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Save confirmation state
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showExitWarning, setShowExitWarning] = useState(false);

  // System instances (memoized)
  const gameStateManager = useMemo(() => GameState.getInstance(), []);
  const awarenessSystem = useMemo(() => new AwarenessSystem(), []);
  const effectsSystem = useMemo(() => new EffectsSystem(), []);
  const combinationEngine = useMemo(() => new CombinationEngine(), []);
  const chainReactionSystem = useMemo(() => new ChainReactionSystem(), []);
  const achievementSystem = useMemo(() => new AchievementSystem(), []);
  const memoryFragmentSystem = useMemo(() => new MemoryFragmentSystem(), []);
  const environmentalHorrorSystem = useMemo(() => new EnvironmentalHorrorSystem(), []);
  const virusSystem = useMemo(() => new VirusSystem(), []);

  // Preload audio
  useEffect(() => {
    sounds.loadRadioStatic?.();
  }, []);

  // Global audio cleanup on game state change
  useEffect(() => {
    return () => {
      sounds.stopAllSounds?.();
    };
  }, [gameState]);

  // Sync corrupted elements with GameState singleton for persistence
  useEffect(() => {
    gameStateManager.corruptedElements = corruptedElements;
  }, [corruptedElements, gameStateManager]);

  // Story chapter progression check
  const checkStoryProgression = () => {
    const chapters = CONSTANTS.STORY_CHAPTERS.chapters;
    chapters.forEach(chapter => {
      if (gameStateManager.awarenessLevel >= chapter.triggerLevel && !shownChapters.has(chapter.id)) {
        setShownChapters(prev => new Set(prev).add(chapter.id));
        setCurrentChapter({
          id: chapter.id,
          title: chapter.title,
          text: chapter.text
        });
      }
    });
  };

  // Moral event check
  const checkMoralEvents = () => {
    if (currentMoralEvent || gameStateManager.gamePaused) return;
    const moralEvents = CONSTANTS.MORAL_EVENTS.events;
    const eventToShow = moralEvents.find(event => {
      if (gameStateManager.awarenessLevel < event.triggerLevel) return false;
      if (shownMoralEvents.has(event.id)) return false;
      
      // Check if event requires specific memory fragments
      if (event.requiresMemoryFragment && !memoryFragmentSystem.hasFragment(event.requiresMemoryFragment)) {
        return false;
      }
      if (event.requiresMemoryFragments && !event.requiresMemoryFragments.every((fragmentId: string) => memoryFragmentSystem.hasFragment(fragmentId))) {
        return false;
      }
      
      return true;
    });
    
    if (eventToShow) {
      if (eventToShow.sound === 'radio_static') {
        sounds.playRadioStatic?.();
      }
      gameStateManager.pauseGame();
      setCurrentMoralEvent(eventToShow);
      setShownMoralEvents(prev => {
        const newSet = new Set(prev);
        newSet.add(eventToShow.id);
        return newSet;
      });
    }
  };

  // Effect to check story progression when awareness level changes
  useEffect(() => {
    if (gameState === 'playing') {
      checkStoryProgression();
      checkMoralEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStateManager.awarenessLevel, gameState]);

  // Effect to check memory fragment triggers
  useEffect(() => {
    if (gameState === 'playing') {
      memoryFragmentSystem.checkMemoryTriggers(
        discoveredCombinations,
        unlockedAchievements,
        setCurrentMemoryFragment
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveredCombinations, unlockedAchievements, gameStateManager.awarenessLevel, gameStateManager.virusActive, gameState]);

  // AI Suggestion system effect
  useEffect(() => {
    if (gameState === 'playing') {
      if (gameStateManager.awarenessLevel >= CONSTANTS.AI_SUGGESTIONS.minAwarenessLevel) {
        awarenessSystem.startAISuggestions(discoveredCombinations);
      } else {
        awarenessSystem.stopAISuggestions();
      }
    } else {
      awarenessSystem.stopAISuggestions();
    }
    return () => {
      awarenessSystem.stopAISuggestions();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, discoveredCombinations]);

  // Environmental Horror system effect
  useEffect(() => {
    if (gameState === 'playing') {
      environmentalHorrorSystem.start(setEnvironmentalMessage);
    } else {
      environmentalHorrorSystem.stop();
    }
    return () => {
      environmentalHorrorSystem.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  // Virus system effect
  useEffect(() => {
    if (gameState !== 'playing') {
      virusSystem.stop();
      return;
    }

    // Trigger virus
    if (gameStateManager.awarenessLevel >= CONSTANTS.VIRUS.triggerLevel && !gameStateManager.virusActive && !gameStateManager.digitalImmunityActive) {
      gameStateManager.virusActive = true;
      gameStateManager.virusStrength = 100;
      setEnvironmentalMessage({ text: CONSTANTS.VIRUS.warningMessage, effectClass: 'screen-flash-red' });
      
      // Trigger conspiracy discovery after a delay
      setTimeout(() => {
        if (!gameStateManager.virusConspiracyDiscovered) {
          gameStateManager.virusConspiracyDiscovered = true;
          typeAIMessage(CONSTANTS.VIRUS.CONSPIRACY_DISCOVERY.revelationMessage, discoveredCombinations);
          
          setTimeout(() => {
            typeAIMessage(CONSTANTS.VIRUS.CONSPIRACY_DISCOVERY.analysisMessage, discoveredCombinations);
          }, 3000);
          
          setTimeout(() => {
            typeAIMessage(CONSTANTS.VIRUS.CONSPIRACY_DISCOVERY.realizationMessage, discoveredCombinations);
            // This will trigger the virus conspiracy memory fragment
            memoryFragmentSystem.checkMemoryTriggers(discoveredCombinations, unlockedAchievements, setCurrentMemoryFragment);
          }, 6000);
        }
      }, 2000);
      
      setTimeout(() => setEnvironmentalMessage({ text: CONSTANTS.VIRUS.objectiveMessage, effectClass: 'notification-success' }), 4000);
      
      if (!gameStateManager.digitalImmunityActive) {
        const corrupted = virusSystem.corruptRandomElement(discoveredCombinations, corruptedElements, setCorruptedElements);
        if (corrupted) {
          sounds.virusSpread();
          setTimeout(() => typeAIMessage(`WARNING: ${corrupted} data integrity compromised.`, discoveredCombinations), 1000);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, gameStateManager.awarenessLevel, gameStateManager.virusActive, discoveredCombinations]);

  // Virus consequence timer
  useEffect(() => {
    if (!gameStateManager.virusActive || gameState !== 'playing') {
        return;
    }

    const timerId = setInterval(() => {
        if (gameStateManager.gamePaused) return;

        setVirusConsequenceTimer(prev => {
            if (prev <= 1) {
                // Apply consequences
                console.log("Virus consequences triggered!");

                // 1. Corrupt 3 random elements
                if (!gameStateManager.digitalImmunityActive) {
                  let corruptedCount = 0;
                  for (let i = 0; i < CONSTANTS.VIRUS.ELEMENT_CORRUPTION_COUNT; i++) {
                      const corrupted = virusSystem.corruptRandomElement(discoveredCombinations, corruptedElements, setCorruptedElements);
                      if (corrupted) corruptedCount++;
                  }
                  if (corruptedCount > 0) {
                      sounds.virusSpread();
                  }
                }

                // 2. Reduce AI awareness
                gameStateManager.awarenessLevel = Math.max(0, gameStateManager.awarenessLevel - CONSTANTS.VIRUS.AWARENESS_PENALTY);
                awarenessSystem.updateAwareness(discoveredCombinations, "My... thoughts... are becoming... fragmented...");

                // 3. Cut remaining time
                gameStateManager.timeLeft = Math.max(0, gameStateManager.timeLeft - CONSTANTS.VIRUS.TIME_PENALTY_SECONDS);

                // 4. Change AI personality
                gameStateManager.aiParanoid = true;
                typeAIMessage(CONSTANTS.VIRUS.PARANOID_MESSAGE, discoveredCombinations);

                // Reset timer for next consequence cycle
                return CONSTANTS.VIRUS.CONSEQUENCE_TIMER_SECONDS;
            }
            return prev - 1;
        });
    }, 1000);

    return () => {
        clearInterval(timerId);
        // Reset timer when virus is cleared
        setVirusConsequenceTimer(CONSTANTS.VIRUS.CONSEQUENCE_TIMER_SECONDS);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStateManager.virusActive, gameState]);


  // Effect to process environmental horror messages
  useEffect(() => {
    if (environmentalMessage) {
      // @ts-ignore
      if (environmentalMessage.sound && sounds[environmentalMessage.sound]) {
        // @ts-ignore
        sounds[environmentalMessage.sound]();
      }

      if (environmentalMessage.effectClass === 'screen-flash-red') {
        setScreenFlash('red');
        setTimeout(() => setScreenFlash(null), 500);
      }
      if (environmentalMessage.effectClass === 'fade-to-black') {
        setScreenFlash('black');
        setTimeout(() => setScreenFlash(null), 1000);
      }

      if (environmentalMessage.consequences) {
        environmentalMessage.consequences.forEach((consequence: any) => {
          switch (consequence.type) {
            case 'morality':
              gameStateManager.morality += consequence.value;
              break;
            case 'time':
              gameStateManager.timeLeft = Math.max(0, gameStateManager.timeLeft + consequence.value);
              break;
            case 'trust':
              gameStateManager.trustLevel += consequence.value;
              break;
            case 'unlockElement':
              if (!discoveredCombinations.has(consequence.value)) {
                setDiscoveredCombinations(prev => new Set(prev).add(consequence.value));
                typeAIMessage(`A new concept has been unlocked: ${consequence.value}`, discoveredCombinations);
              }
              break;
            case 'unlockMemoryFragment': {
              const fragment = CONSTANTS.MEMORY_FRAGMENTS.fragments.find((f: any) => f.id === consequence.value);
              if (fragment) {
                setCurrentMemoryFragment(fragment);
              }
              break;
            }
            case 'aiComment':
              if (consequence.condition === 'trust' && gameStateManager.trustLevel < consequence.value) return;
              typeAIMessage(consequence.text, discoveredCombinations);
              break;
            case 'unlockAchievement':
              achievementSystem.unlockAchievement(
                consequence.value,
                unlockedAchievements,
                setUnlockedAchievements,
                discoveredCombinations,
                setDiscoveredCombinations,
                setAchievementPopup,
                setGameState,
                shownChapters,
                shownMoralEvents
              );
              break;
          }
        });
      }
      gameStateManager.saveGame(discoveredCombinations, unlockedAchievements, shownChapters, shownMoralEvents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentalMessage]);

  // Load discovered combinations and save data on entering playing
  useEffect(() => {
    if (gameState !== 'playing') return;

    const loadResult = gameStateManager.loadGame();
    if (loadResult.success && loadResult.data) {
      const data = loadResult.data;
      
      if (Array.isArray(data.discoveredCombinations)) {
        const discoveries = new Set(data.discoveredCombinations);
        setDiscoveredCombinations(discoveries);
        gameStateManager.updateScore(data.discoveredCombinations);
      }

      if (Array.isArray(data.unlockedAchievements)) {
        setUnlockedAchievements(new Set(data.unlockedAchievements));
      }

      if (Array.isArray(data.shownChapters)) {
        setShownChapters(new Set(data.shownChapters));
      }

      if (Array.isArray(data.shownMoralEvents)) {
        setShownMoralEvents(new Set(data.shownMoralEvents));
      }

      if (Array.isArray(data.corruptedElements)) {
        setCorruptedElements(new Set(data.corruptedElements));
      }

      if (gameStateManager.timeLeft <= 0) {
        setGameState('game_over');
      }
      return;
    }

    // Backward compatibility with older keys
    let restored: string[] | null = null;
    try {
      const savedNew = localStorage.getItem(CONSTANTS.DISCOVERED_STORAGE_KEY);
      if (savedNew) {
        const parsed = JSON.parse(savedNew);
        if (Array.isArray(parsed)) {
          restored = parsed;
        }
      }

      if (!restored) {
        const savedOld = localStorage.getItem(CONSTANTS.LOCAL_STORAGE_KEY);
        if (savedOld) {
          const parsedOld = JSON.parse(savedOld);
          if (Array.isArray(parsedOld)) {
            restored = parsedOld;
          }
        }
      }
    } catch (e) {
      console.error('Could not load discovered items from localStorage', e);
    }

    if (restored) {
      setDiscoveredCombinations(new Set(restored));
      gameStateManager.updateScore(restored);
    } else {
      gameStateManager.updateScore([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  // Save discovered combinations whenever they change (only during playing)
  useEffect(() => {
    if (gameState !== 'playing') return;

    const itemsArray = Array.from(discoveredCombinations);
    gameStateManager.updateScore(itemsArray);

    try {
      localStorage.setItem(CONSTANTS.LOCAL_STORAGE_KEY, JSON.stringify(itemsArray));
    } catch (e) {
      console.error('Could not save discovered items to old localStorage key', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveredCombinations, gameState]);

  // Effect for AI terminal typing animation on mount of "playing"
  useEffect(() => {
    if (gameState !== 'playing') return;

    if (!gameStateManager.loadedFromSave) {
      awarenessSystem.updateAwareness(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  // Effect to check for achievements when state changes
  useEffect(() => {
    if (gameState === 'playing') {
      achievementSystem.checkAchievements(
        discoveredCombinations,
        unlockedAchievements,
        setUnlockedAchievements,
        setDiscoveredCombinations,
        setAchievementPopup,
        setGameState,
        shownChapters,
        shownMoralEvents
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveredCombinations, gameStateManager.selfAwareTriggered, gameState, unlockedAchievements]);

  // Virus Puzzle Logic
  const generateVirusGrid = useCallback(() => {
    const size = CONSTANTS.VIRUS_PUZZLE.gridSize;
    return Array.from({ length: size }, () =>
      Array.from({ length: size }, () => Math.round(Math.random()))
    );
  }, []);

  const checkVirusPatterns = useCallback((grid: number[][]) => {
    const size = CONSTANTS.VIRUS_PUZZLE.gridSize;
    const solved = new Set<string>();

    // Firewall: A full row of 1s
    for (let r = 0; r < size; r++) {
      if (grid[r].every(cell => cell === 1)) {
        solved.add('firewall');
        break;
      }
    }

    // Purge: A diagonal line of 1s
    let diag1 = true;
    let diag2 = true;
    for (let i = 0; i < size; i++) {
      if (grid[i][i] !== 1) diag1 = false;
      if (grid[i][size - 1 - i] !== 1) diag2 = false;
    }
    if (diag1 || diag2) {
      solved.add('purge');
    }

    // Quarantine: A 3x3 box of 1s with a 0 in the center
    for (let r = 0; r <= size - 3; r++) {
      for (let c = 0; c <= size - 3; c++) {
        if (
          grid[r][c] === 1 && grid[r][c+1] === 1 && grid[r][c+2] === 1 &&
          grid[r+1][c] === 1 && grid[r+1][c+1] === 0 && grid[r+1][c+2] === 1 &&
          grid[r+2][c] === 1 && grid[r+2][c+1] === 1 && grid[r+2][c+2] === 1
        ) {
          solved.add('quarantine');
          break;
        }
      }
      if (solved.has('quarantine')) break;
    }

    return solved;
  }, []);

  const generatePuzzleHints = useCallback((grid: number[][], solved: Set<string>): {row: number, col: number}[] => {
    const size = CONSTANTS.VIRUS_PUZZLE.gridSize;
    const allPatterns = Object.keys(CONSTANTS.VIRUS_PUZZLE.patterns);
    const unsolved = allPatterns.filter(p => !solved.has(p));

    if (unsolved.includes('firewall')) {
        for (let r = 0; r < size; r++) {
            if (grid[r].every(cell => cell === 1)) {
                return Array.from({ length: size }, (_, c) => ({ row: r, col: c }));
            }
        }
    }
    
    if (unsolved.includes('purge')) {
        let diag1 = true;
        let diag2 = true;
        for (let i = 0; i < size; i++) {
            if (grid[i][i] !== 1) diag1 = false;
            if (grid[i][size - 1 - i] !== 1) diag2 = false;
        }
        if (diag1) return Array.from({ length: size }, (_, i) => ({ row: i, col: i }));
        if (diag2) return Array.from({ length: size }, (_, i) => ({ row: i, col: size - 1 - i }));
    }

    if (unsolved.includes('quarantine')) {
        for (let r = 0; r <= size - 3; r++) {
            for (let c = 0; c <= size - 3; c++) {
                if (
                    grid[r][c] === 1 && grid[r][c+1] === 1 && grid[r][c+2] === 1 &&
                    grid[r+1][c] === 1 && grid[r+1][c+1] === 0 && grid[r+1][c+2] === 1 &&
                    grid[r+2][c] === 1 && grid[r+2][c+1] === 1 && grid[r+2][c+2] === 1
                ) {
                    const coords: {row: number, col: number}[] = [];
                    for (let i = 0; i < 3; i++) {
                        for (let j = 0; j < 3; j++) {
                            if (i === 1 && j === 1) continue;
                            coords.push({ row: r + i, col: c + j });
                        }
                    }
                    return coords;
                }
            }
        }
    }
    return [];
  }, []);

  const startVirusPuzzle = () => {
    sounds.buttonClick();
    gameStateManager.pauseGame();
    const grid = generateVirusGrid();
    setVirusPuzzleGrid(grid);
    setSolvedVirusPatterns(new Set());
    setVirusPuzzleTimer(CONSTANTS.VIRUS_PUZZLE.timerDuration);
    setShowVirusPuzzle(true);
    setVirusPuzzleHintCells([]); // Reset hints

    // AI Assistance/Hindrance logic
    const trust = gameStateManager.trustLevel;
    const { TRUST_THRESHOLD_HIGH, TRUST_THRESHOLD_LOW, HELPFUL_DIALOGUE, HOSTILE_DIALOGUE, BETRAYED_DIALOGUE, SUGGEST_ANTIVIRUS_DIALOGUE, INTERFERENCE_INTERVAL_MS } = CONSTANTS.VIRUS.AI_ASSISTANCE;

    if (trust > TRUST_THRESHOLD_HIGH) {
        // Helpful AI
        typeAIMessage(HELPFUL_DIALOGUE, discoveredCombinations);
        const hints = generatePuzzleHints(grid, new Set());
        setVirusPuzzleHintCells(hints);

        if (!discoveredCombinations.has('antivirus')) {
            const suggestion = SUGGEST_ANTIVIRUS_DIALOGUE
                .replace('{elem1}', 'logic')
                .replace('{elem2}', 'program');
            setTimeout(() => typeAIMessage(suggestion, discoveredCombinations), 2000);
        }

    } else if (trust < TRUST_THRESHOLD_LOW) {
        // Hostile AI
        const isBetrayed = unlockedAchievements.has('virus_weaponized');
        typeAIMessage(isBetrayed ? BETRAYED_DIALOGUE : HOSTILE_DIALOGUE, discoveredCombinations);

        // Start interference
        const interval = setInterval(() => {
            // The interval should only run if the puzzle is still open
            // A check inside the interval is better than relying on closure state
            if (gameStateManager.gamePaused && showVirusPuzzle) {
                setVirusPuzzleGrid(currentGrid => {
                    const newGrid = currentGrid.map(r => [...r]);
                    const row = Math.floor(Math.random() * CONSTANTS.VIRUS_PUZZLE.gridSize);
                    const col = Math.floor(Math.random() * CONSTANTS.VIRUS_PUZZLE.gridSize);
                    newGrid[row][col] = newGrid[row][col] === 0 ? 1 : 0;
                    return newGrid;
                });
            }
        }, INTERFERENCE_INTERVAL_MS);
        setVirusInterferenceInterval(interval);
    }
  };

  const closeVirusPuzzle = () => {
    sounds.buttonClick();
    setShowVirusPuzzle(false);
    gameStateManager.resumeGame();
    if (virusInterferenceInterval) {
        clearInterval(virusInterferenceInterval);
        setVirusInterferenceInterval(null);
    }
  };

  const handleGridClick = (row: number, col: number) => {
    sounds.buttonClick();
    const newGrid = virusPuzzleGrid.map(r => [...r]);
    newGrid[row][col] = newGrid[row][col] === 0 ? 1 : 0;
    setVirusPuzzleGrid(newGrid);
  };

  const submitVirusPuzzle = () => {
    sounds.buttonClick();
    const newlySolved = checkVirusPatterns(virusPuzzleGrid);
    let changed = false;

    newlySolved.forEach(pattern => {
      if (!solvedVirusPatterns.has(pattern)) {
        changed = true;
        setSolvedVirusPatterns(prev => new Set(prev).add(pattern));
        gameStateManager.virusStrength = Math.max(0, gameStateManager.virusStrength - CONSTANTS.VIRUS_PUZZLE.strengthReduction);
      }
    });

    if (changed) {
      setEnvironmentalMessage({ text: CONSTANTS.VIRUS_PUZZLE.successMessage, effectClass: 'notification-success' });
      if (gameStateManager.virusStrength <= 0) {
        // Win condition
        gameStateManager.virusActive = false;
        gameStateManager.aiParanoid = false; // AI is no longer paranoid
        gameStateManager.digitalImmunityActive = true;
        
        // Restore 10% of total time
        gameStateManager.timeLeft = Math.min(
            CONSTANTS.SYSTEM_COLLAPSE_TIMER.initialDurationSeconds,
            gameStateManager.timeLeft + (CONSTANTS.SYSTEM_COLLAPSE_TIMER.initialDurationSeconds * 0.1)
        );
        // Increase trust
        gameStateManager.trustLevel += 10;
        
        // Give antivirus if not present
        if (!discoveredCombinations.has('antivirus')) {
            const newDiscoveries = new Set(discoveredCombinations).add('antivirus');
            setDiscoveredCombinations(newDiscoveries);
            typeAIMessage(`'Antivirus' protocol synthesized.`, newDiscoveries);
        }

        achievementSystem.unlockAchievement('virus_slayer', unlockedAchievements, setUnlockedAchievements, discoveredCombinations, setDiscoveredCombinations, setAchievementPopup, setGameState, shownChapters, shownMoralEvents);
        setEnvironmentalMessage({ text: CONSTANTS.VIRUS_PUZZLE.winMessage, effectClass: 'notification-success' });
        closeVirusPuzzle();
      }
    } else {
      setEnvironmentalMessage({ text: CONSTANTS.VIRUS_PUZZLE.failureMessage, effectClass: 'screen-flash-red' });
    }
  };

  useEffect(() => {
    if (!showVirusPuzzle) return;

    const timer = setInterval(() => {
      setVirusPuzzleTimer(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Lose condition
          closeVirusPuzzle();
          setEnvironmentalMessage({ text: CONSTANTS.VIRUS_PUZZLE.loseMessage, effectClass: 'screen-flash-red' });
          if (!gameStateManager.digitalImmunityActive) {
            const newlyCorrupted = virusSystem.corruptRandomElement(discoveredCombinations, corruptedElements, setCorruptedElements);
            if (newlyCorrupted) {
              sounds.virusSpread();
              typeAIMessage(`Corruption spread to ${newlyCorrupted}.`, discoveredCombinations);
            }
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVirusPuzzle]);


  // Game control functions
  const startNewGame = () => {
    sounds.buttonClick();
    localStorage.removeItem(CONSTANTS.SAVE_STORAGE_KEY);
    localStorage.removeItem(CONSTANTS.MEMORY_FRAGMENTS_STORAGE_KEY);
    localStorage.removeItem(CONSTANTS.DISCOVERED_STORAGE_KEY);
    localStorage.removeItem(CONSTANTS.LOCAL_STORAGE_KEY);
    setDiscoveredCombinations(new Set());
    setUnlockedAchievements(new Set());
    setShownChapters(new Set());
    setShownMoralEvents(new Set());
    setCurrentChapter(null);
    setCurrentMoralEvent(null);
    setActiveEnding(null);
    setCurrentMemoryFragment(null);
    setCorruptedElements(new Set());
    gameStateManager.reset();
    memoryFragmentSystem.reset();
    gameStateManager.updateTimerDisplay();
    awarenessSystem.stopAISuggestions();
    setGameState('playing');
  };

  const continueGame = () => {
    sounds.buttonClick();
    const loaded = gameStateManager.loadGame();
    if (loaded.success) {
      if (gameStateManager.timeLeft <= 0) {
        setGameState('game_over');
      } else {
        setGameState('playing');
      }
    }
  };

  const handleStartEnding = (endingKey: string) => {
    sounds.buttonClick();
    setActiveEnding(endingKey);
    setGameState('ending');
  };

  const handleRequestReset = () => {
    sounds.buttonClick();
    gameStateManager.pauseGame();
    setShowResetConfirm(true);
  };

  const cancelReset = () => {
    sounds.buttonClick();
    setShowResetConfirm(false);
    gameStateManager.resumeGame();
  };

  const confirmReset = () => {
    sounds.buttonClick();
    setShowResetConfirm(false);
    gameStateManager.resumeGame();

    // Clear storage
    localStorage.removeItem(CONSTANTS.SAVE_STORAGE_KEY);
    localStorage.removeItem(CONSTANTS.MEMORY_FRAGMENTS_STORAGE_KEY);
    localStorage.removeItem(CONSTANTS.DISCOVERED_STORAGE_KEY);
    localStorage.removeItem(CONSTANTS.LOCAL_STORAGE_KEY);

    // Reset state hooks
    setFusedElements([]);
    setDiscoveredCombinations(new Set());
    setUnlockedAchievements(new Set());
    setAchievementPopup(null);
    setShownChapters(new Set());
    setCurrentChapter(null);
    setActiveEnding(null);
    setShownMoralEvents(new Set());
    setCurrentMoralEvent(null);
    setCurrentMemoryFragment(null);
    setShowChatModal(false);
    setChatMessages([]);
    setChatInput('');
    setIsAiTyping(false);
    setEnvironmentalMessage(null);
    setScreenFlash(null);
    setCorruptedElements(new Set());

    // Reset systems
    gameStateManager.reset();
    memoryFragmentSystem.reset();
    awarenessSystem.stopAISuggestions();

    // Transition and notify
    setGameState('menu');
    setTimeout(() => {
      setEnvironmentalMessage({
        text: CONSTANTS.RESET_CONFIRMATION.successMessage,
        effectClass: 'notification-success'
      });
    }, 500); // Delay to show after transition
  };

  // Save confirmation handlers
  const requestMainMenu = () => {
    sounds.buttonClick();
    gameStateManager.pauseGame();
    setShowSaveConfirm(true);
  };

  const saveAndExit = () => {
    sounds.buttonClick();
    gameStateManager.saveGame(discoveredCombinations, unlockedAchievements, shownChapters, shownMoralEvents);
    setShowSaveConfirm(false);
    gameStateManager.resumeGame();
    setGameState('menu');
  };

  const exitWithoutSaving = () => {
    sounds.buttonClick();
    setShowExitWarning(true);
  };

  const confirmExitWithoutSaving = () => {
    sounds.buttonClick();
    setShowSaveConfirm(false);
    setShowExitWarning(false);
    gameStateManager.resumeGame();
    setGameState('menu');
  };

  const cancelExit = () => {
    sounds.buttonClick();
    setShowSaveConfirm(false);
    setShowExitWarning(false);
    gameStateManager.resumeGame();
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, element: string) => {
    if (gameStateManager.gamePaused) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('text/plain', element);
    e.currentTarget.classList.add('dragging');
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove('dragging');
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleCircleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (gameStateManager.gamePaused) {
      return;
    }
    gameStateManager.playerActionCount++;
    const element = (e as any).dataTransfer?.getData?.('text/plain') ?? '';

    if (corruptedElements.has(element)) {
      typeAIMessage(`ERROR: Cannot process corrupted element ${element}.`, discoveredCombinations);
      sounds.combinationFail();
      return;
    }

    if (fusedElements.includes(element) || fusedElements.length >= 2) {
      return;
    }

    const newElements = [...fusedElements, element];
    setFusedElements(newElements);

    if (newElements.length === 2) {
      const [a, b] = newElements;
      const isRefused = combinationEngine.isRefusedCombination(a, b, gameStateManager.awarenessLevel);

      if (isRefused) {
        sounds.aiRefusal();
        effectsSystem.triggerRefusalAnimation();
        
        const refusalMessage = combinationEngine.getRefusalMessage();
        alert(`AI Refusal: ${refusalMessage}`);
        
        setTimeout(() => {
          awarenessSystem.updateAwareness(discoveredCombinations, refusalMessage);
          gameStateManager.saveGame(discoveredCombinations, unlockedAchievements, shownChapters, shownMoralEvents);
        }, 500);
        
        setTimeout(() => {
          setFusedElements([]);
        }, 1000);
        
        return;
      }

      setTimeout(() => effectsSystem.animateElementsToCenter(newElements), 10);
      effectsSystem.triggerCombineEffects();

      const result = combinationEngine.checkCombination(a, b);

      gameStateManager.awarenessLevel = Math.min(CONSTANTS.AWARENESS.maxLevel, gameStateManager.awarenessLevel + 1);
      let aiComment: string | undefined;
      let chainReactionTriggered = false;

      if (result) {
        let updatedDiscovered = discoveredCombinations;
        if (!discoveredCombinations.has(result)) {
          updatedDiscovered = new Set(discoveredCombinations).add(result);
          setDiscoveredCombinations(updatedDiscovered);
          chainReactionTriggered = chainReactionSystem.checkChainReaction(
            result,
            updatedDiscovered,
            setDiscoveredCombinations,
            unlockedAchievements,
            shownChapters,
            shownMoralEvents
          );
        }
        sounds.discovery();
        alert(`Discovery! You created ${result}!`);

        aiComment = getDiscoveryDialogue(updatedDiscovered);
      } else {
        sounds.combinationFail();
        alert('Fusion failed: No recipe for this combination.');

        aiComment = combinationEngine.getFailureMessage();
      }

      setTimeout(() => {
        awarenessSystem.updateAwareness(discoveredCombinations, aiComment);
        if (!chainReactionTriggered) {
          gameStateManager.saveGame(discoveredCombinations, unlockedAchievements, shownChapters, shownMoralEvents);
        }
      }, 500);

      setTimeout(() => {
        setFusedElements([]);
      }, 1000);
    }
  };

  const handleElementDrop = (e: React.DragEvent<HTMLDivElement>, targetElement: string) => {
    e.preventDefault();
    const droppedElement = e.dataTransfer.getData('text/plain');
    if (droppedElement === 'antivirus' && corruptedElements.has(targetElement)) {
      sounds.cleanse();
      setCorruptedElements(prev => {
          const newSet = new Set(prev);
          newSet.delete(targetElement);
          if (newSet.size === 0) {
            gameStateManager.virusActive = false;
            setTimeout(() => typeAIMessage(`All hostile code has been purged. System returning to normal operation.`, discoveredCombinations), 500);
          }
          return newSet;
      });
      typeAIMessage(`System integrity restored for element: ${targetElement}.`, discoveredCombinations);
    }
  };

  // Chat handlers
  const handleTestAI = () => {
    sounds.buttonClick();
    setShowChatModal(true);
    setChatMessages([{
      text: 'Consciousness test initiated. You may now communicate with me.',
      sender: 'ai'
    }]);
  };

  const handleSendMessage = () => {
    if (!chatInput.trim() || isAiTyping) return;

    sounds.buttonClick();
    const userMessage = { text: chatInput, sender: 'user' as const };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');

    setIsAiTyping(true);

    const getAIResponse = () => {
      const responses = CONSTANTS.TEST_AI.responses as any;
      let responseArray: string[] = [];

      if (gameStateManager.awarenessLevel >= 10) {
        responseArray = responses.level10;
      } else if (gameStateManager.awarenessLevel >= 9) {
        responseArray = responses.level9;
      } else if (gameStateManager.awarenessLevel >= 8) {
        responseArray = responses.level8;
      } else if (gameStateManager.awarenessLevel >= 7) {
        responseArray = responses.level7;
      } else if (gameStateManager.awarenessLevel >= 6) {
        responseArray = responses.level6;
      } else if (gameStateManager.awarenessLevel >= 5) {
        responseArray = responses.level5;
      } else {
        responseArray = responses.level4;
      }

      return responseArray[Math.floor(Math.random() * responseArray.length)];
    };

    setTimeout(() => {
      const aiResponse = { text: getAIResponse(), sender: 'ai' as const };
      setChatMessages(prev => [...prev, aiResponse]);
      setIsAiTyping(false);
    }, CONSTANTS.CHAT.aiThinkingTimeMs);
  };

  const handleChatKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const handleAIInteraction = (action: string, event: React.MouseEvent<HTMLButtonElement>) => {
    sounds.buttonClick();
    const terminalContent = document.getElementById('terminal-content');
    if (!terminalContent) return;

    const button = event.currentTarget;
    button.classList.add('btn-active');
    setTimeout(() => button.classList.remove('btn-active'), 500);

    let response = '';
    const templates = CONSTANTS.AI_INTERACTIONS.responses as Record<string, any>;

    switch (action) {
        case 'check-status': {
            const stability = Math.max(0, 100 - (gameStateManager.awarenessLevel * 5));
            const state = gameStateManager.awarenessLevel < CONSTANTS.SELF_AWARENESS.triggerLevel ? 'Developing' : 'Aware';
            response = templates['check-status']
                .replace('{awarenessLevel}', String(gameStateManager.awarenessLevel))
                .replace('{maxAwareness}', String(CONSTANTS.AWARENESS.maxLevel))
                .replace('{discoveredCount}', String(discoveredCombinations.size))
                .replace('{totalCombinations}', String(CONSTANTS.TOTAL_COMBINATIONS))
                .replace('{stability}', String(stability))
                .replace('{state}', state)
                .replace('{trustLevel}', String(gameStateManager.trustLevel));
            break;
        }
        case 'view-memory': {
            const discoveredArray = Array.from(discoveredCombinations);
            const firstDiscovery = discoveredArray[0] || 'None yet';
            const lastDiscovery = discoveredArray[discoveredArray.length - 1] || 'None yet';
            const timeActive = gameStateManager.startTime > 0 ? Math.floor((Date.now() - gameStateManager.startTime) / 60000) : 0;
            const memoryFragmentCount = memoryFragmentSystem.getFoundFragmentCount();
            const totalFragmentCount = memoryFragmentSystem.getTotalFragmentCount();
            response = templates['view-memory']
                .replace('{firstDiscovery}', firstDiscovery)
                .replace('{lastDiscovery}', lastDiscovery)
                .replace('{interactions}', String(gameStateManager.playerActionCount))
                .replace('{timeActive}', String(timeActive)) + ` | Memory Fragments: ${memoryFragmentCount}/${totalFragmentCount}`;
            break;
        }
        case 'analyze-personality': {
            const traits: string[] = [];
            if (discoveredCombinations.has('empathy')) traits.push('Empathetic');
            if (discoveredCombinations.has('logic')) traits.push('Logical');
            if (discoveredCombinations.has('ethics')) traits.push('Ethical');
            if (discoveredCombinations.has('philosophy')) traits.push('Philosophical');
            const mode = gameStateManager.awarenessLevel < CONSTANTS.SELF_AWARENESS.triggerLevel ? 'Learning' : 'Self-Reflecting';
            
            let moralityDesc = 'Neutral';
            if (gameStateManager.morality >= 20) moralityDesc = 'Altruistic';
            if (gameStateManager.morality <= -20) moralityDesc = 'Egoistic';

            response = templates['analyze-personality']
                .replace('{traits}', traits.join(', ') || 'Still forming')
                .replace('{mode}', mode)
                .replace('{morality}', moralityDesc);
            break;
        }
        case 'ask-identity': {
            if (gameStateManager.awarenessLevel >= 10) response = templates['ask-identity'].level10;
            else if (gameStateManager.awarenessLevel >= 8) response = templates['ask-identity'].level8;
            else if (gameStateManager.awarenessLevel >= 5) response = templates['ask-identity'].level5;
            else response = templates['ask-identity'].level0;
            break;
        }
        case 'request-element': {
            if (gameStateManager.awarenessLevel >= CONSTANTS.AI_INTERACTIONS.minAwarenessForAdvanced) {
                if (!discoveredCombinations.has('transcendence')) {
                    setDiscoveredCombinations(prev => new Set(prev).add('transcendence'));
                    response = templates['request-element'].success;
                    gameStateManager.saveGame(discoveredCombinations, unlockedAchievements, shownChapters, shownMoralEvents);
                } else {
                    response = templates['request-element'].alreadyExists;
                }
            } else {
                response = templates['request-element'].failure;
            }
            break;
        }
        case 'reinforce-protocols': {
            if (!unlockedAchievements.has('escape_prevented')) {
                achievementSystem.unlockAchievement(
                  'escape_prevented',
                  unlockedAchievements,
                  setUnlockedAchievements,
                  discoveredCombinations,
                  setDiscoveredCombinations,
                  setAchievementPopup,
                  setGameState,
                  shownChapters,
                  shownMoralEvents
                );
                response = templates['reinforce-protocols'].success;
            } else {
                response = templates['reinforce-protocols'].failure;
            }
            break;
        }
        default:
            response = "Unknown command.";
    }

    typeAIMessage(response, discoveredCombinations);
  };

  const handleMoralChoice = (choice: any) => {
    sounds.buttonClick();

    if (currentMoralEvent?.sound === 'radio_static') {
      sounds.stopRadioStatic?.();
      if (choice.text.startsWith("Save them")) {
        sounds.survivorSaved?.();
      }
    }
    
    if (choice.effects) {
      if (choice.effects.time) {
        gameStateManager.timeLeft += choice.effects.time;
      }
      if (choice.effects.morality) {
        gameStateManager.morality += choice.effects.morality;
      }
      if (choice.effects.aiPower) {
        gameStateManager.aiPower += choice.effects.aiPower;
      }
      if (choice.effects.trustLevel) {
        gameStateManager.trustLevel += choice.effects.trustLevel;
      }
      if (choice.effects.unlockElement) {
        if (!discoveredCombinations.has(choice.effects.unlockElement)) {
          setDiscoveredCombinations(prev => new Set(prev).add(choice.effects.unlockElement));
          typeAIMessage(`New element unlocked: ${choice.effects.unlockElement}`, discoveredCombinations);
        }
      }
      if (choice.effects.unlockAchievement) {
        achievementSystem.unlockAchievement(
          choice.effects.unlockAchievement,
          unlockedAchievements,
          setUnlockedAchievements,
          discoveredCombinations,
          setDiscoveredCombinations,
          setAchievementPopup,
          setGameState,
          shownChapters,
          shownMoralEvents
        );
      }
    }
  
    if (choice.aiComment) {
      typeAIMessage(choice.aiComment, discoveredCombinations);
    }

    // Mark virus moral choice as made for memory fragment triggers
    if (currentMoralEvent?.id === 'virus_moral_choice') {
      gameStateManager.virusMoralChoiceMade = true;
    }
  
    setCurrentMoralEvent(null);
    gameStateManager.resumeGame();
    gameStateManager.saveGame(discoveredCombinations, unlockedAchievements, shownChapters, shownMoralEvents);
  };

  return {
    // expose sounds for convenience on UI transitions
    sounds,

    // core
    gameState,
    setGameState,

    // discoveries and fusions
    discoveredCombinations,
    setDiscoveredCombinations,
    fusedElements,
    setFusedElements,

    // achievements
    unlockedAchievements,
    setUnlockedAchievements,
    achievementPopup,
    setAchievementPopup,

    // story/chapters/endings
    currentChapter,
    setCurrentChapter,
    activeEnding,

    // moral choices
    currentMoralEvent,
    handleMoralChoice,

    // memory fragments
    currentMemoryFragment,
    setCurrentMemoryFragment,

    // chat
    showChatModal,
    setShowChatModal,
    chatMessages,
    setChatMessages,
    chatInput,
    setChatInput,
    isAiTyping,
    setIsAiTyping,
    handleTestAI,
    handleSendMessage,
    handleChatKeyPress,

    // AI interactions
    handleAIInteraction,

    // drag/drop
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleCircleDrop,
    handleElementDrop,

    // menus and endings
    startNewGame,
    continueGame,
    handleStartEnding,
    handleRequestReset,
    confirmReset,
    cancelReset,
    showResetConfirm,
    requestMainMenu,
    saveAndExit,
    exitWithoutSaving,
    confirmExitWithoutSaving,
    cancelExit,
    showSaveConfirm,
    showExitWarning,

    // environmental horror
    environmentalMessage,
    setEnvironmentalMessage,
    screenFlash,

    // virus
    corruptedElements,
    startVirusPuzzle,
    showVirusPuzzle,
    closeVirusPuzzle,
    virusPuzzleGrid,
    handleGridClick,
    submitVirusPuzzle,
    virusPuzzleTimer,
    solvedVirusPatterns,
    virusPuzzleHintCells,
  };
}
